import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuthStore } from "../../../stores/authStore";

const LOCKOUT_STORAGE_KEY = "time";
const LOCKOUT_DURATION_MS = 5 * 60 * 1000;

const parseLockoutExpiry = (value: string | null) => {
    if (!value) return null;

    const timestamp = Number(value);
    if (!Number.isNaN(timestamp) && timestamp > 0) {
        return timestamp;
    }

    const parsedDate = new Date(value).getTime();
    return Number.isNaN(parsedDate) ? null : parsedDate;
};

const formatRemainingTime = (remainingMs: number) => {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");

    return `${minutes}:${seconds}`;
};

const useLoginLockout = () => {
    const loginCount = useAuthStore((s) => s.loginCount);
    const resetLoginCount = useAuthStore((s) => s.resetLoginCount);
    const [lockoutExpiry, setLockoutExpiry] = useState<number | null>(() =>
        parseLockoutExpiry(localStorage.getItem(LOCKOUT_STORAGE_KEY)),
    );
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (loginCount < 5) return;

        const expiry = Date.now() + LOCKOUT_DURATION_MS;
        localStorage.setItem(LOCKOUT_STORAGE_KEY, String(expiry));
        setLockoutExpiry(expiry);
        resetLoginCount();
    }, [loginCount, resetLoginCount]);

    useEffect(() => {
        if (!lockoutExpiry) return;

        const tick = () => {
            const currentTime = Date.now();

            if (currentTime >= lockoutExpiry) {
                localStorage.removeItem(LOCKOUT_STORAGE_KEY);
                setLockoutExpiry(null);
                setNow(currentTime);
                return;
            }

            setNow(currentTime);
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);

        return () => window.clearInterval(intervalId);
    }, [lockoutExpiry]);

    const remainingMs = useMemo(() => {
        if (!lockoutExpiry) return 0;
        return Math.max(0, lockoutExpiry - now);
    }, [lockoutExpiry, now]);

    const isLocked = remainingMs > 0;
    const countdownLabel = isLocked
        ? formatRemainingTime(remainingMs)
        : "Masuk";

    const showLockedAlert = () => {
        Swal.fire({
            title: "Silahkan menunnggu!",
            text: "Anda harus menunggu sebelum bisa mencoba masuk kembali",
            icon: "error",
            confirmButtonColor: "#45A779",
            customClass: {
                container: "my-swal",
            },
        });
    };

    return {
        countdownLabel,
        isLocked,
        showLockedAlert,
    };
};

export default useLoginLockout;
